import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import Papa from "papaparse";
import { js2xml } from "xml-js";
import { AlertCircle, CheckCircle2, Copy, RotateCcw } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { copyToClipboard } from "@/lib/utils";

const SAMPLE_JSON_VALIDATOR = `{
  "siteName": "247 DevTools Hub",
  "seoReady": true,
  "metadata": {
    "apiVersion": "v2.4.0",
    "environment": "Client-Side (Privacy Shield)",
    "localMode": true
  },
  "supportedOutputFormats": [
    "JSON",
    "CSV",
    "XML",
    "YAML"
  ],
  "latencyMs": 0,
  "systemHealth": {
    "status": "operational",
    "loadAverage": 0.12
  }
}`;

export function JsonValidator() {
  const [input, setInput] = useState(SAMPLE_JSON_VALIDATOR);
  const [error, setError] = useState<string | null>(null);

  const validate = () => {
    if (!input.trim()) { setError(null); return; }
    try {
      JSON.parse(input);
      setError("Valid JSON");
    } catch (e: any) {
      setError(e.message);
    }
  };

  useEffect(() => {
    validate();
  }, []);

  return (
    <ToolPageLayout
      title="JSON Validator & Linter Online - Check JSON Syntax"
      description="Validate and lint your JSON code instantly. Check for syntax errors, missing brackets, trailing commas, or quotes. 100% private and processed entirely in-browser."
      h1="JSON Validator"
      category="JSON / Data"
      categoryPath="/csv-editor"
      howToUse={[
        "Paste or type your raw JSON text into the input area below.",
        "Click 'Validate JSON Code Now' to analyze syntax.",
        "See immediate green/red diagnostic alerts on nesting, braces, or missing attributes."
      ]}
      faq={[
        { question: "How does the JSON validator work?", answer: "It parses your string input using a secure local Javascript engine. If formatting exceptions exist, it points to the precise line number." },
        { question: "Is my JSON structure secure?", answer: "Yes, all data processing occurs on your client machine in RAM. No information is transmitted to any cloud servers." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <label className="text-sm font-semibold text-slate-700">Input JSON Code</label>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { setInput(SAMPLE_JSON_VALIDATOR); setTimeout(validate, 50); }}
            className="text-xs py-1 h-7 gap-1"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset Sample
          </Button>
        </div>
        <Textarea 
          value={input} 
          onChange={e => setInput(e.target.value)} 
          placeholder="Paste JSON here..." 
          className="min-h-[250px] font-mono text-xs" 
        />
        <Button onClick={validate} className="w-full bg-slate-900 hover:bg-slate-800 text-white">Validate JSON Code Now</Button>
        {error && (
          <div className={`p-4 rounded-xl flex items-center gap-2 text-sm font-medium ${error === "Valid JSON" ? "bg-emerald-50 text-emerald-800 border border-emerald-100" : "bg-red-50 text-red-800 border border-red-100"}`}>
            {error === "Valid JSON" ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertCircle className="w-5 h-5 text-red-600" />}
            {error}
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_JSON_ARRAY = `[
  { "id": 101, "product": "Laptop Pro 16\\"", "price": 1299.99, "quantity": 15, "inStock": true, "category": "Hardware" },
  { "id": 102, "product": "Wireless Keyboard", "price": 79.50, "quantity": 40, "inStock": true, "category": "Accessories" },
  { "id": 103, "product": "Bluetooth Ergonomic Mouse", "price": 49.99, "quantity": 0, "inStock": false, "category": "Accessories" },
  { "id": 104, "product": "USB-C Dual Docking Station", "price": 149.00, "quantity": 22, "inStock": true, "category": "Connectivity" },
  { "id": 105, "product": "UltraWide 4K IPS Monitor 34\\"", "price": 449.99, "quantity": 8, "inStock": true, "category": "Hardware" }
]`;

export function JsonToCsvConverter() {
  const [input, setInput] = useState(SAMPLE_JSON_ARRAY);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const process = () => {
    try {
      const parsed = JSON.parse(input);
      setOutput(Papa.unparse(parsed));
    } catch(e:any) {
      setOutput("Error: " + e.message);
    }
  };

  useEffect(() => {
    process();
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="JSON to CSV Converter Online - Export JSON to Excel"
      description="Convert list arrays of structured JSON objects into neat, comma-separated CSV rows ready for spreadsheets like Microsoft Excel or Google Sheets."
      h1="JSON to CSV Converter"
      category="JSON / Data"
      categoryPath="/csv-editor"
      howToUse={[
        "Input or paste your JSON array list into the editor.",
        "The converter immediately parses keys into columns and values into table rows.",
        "Copy your CSV text or export it to excel cleanly."
      ]}
      faq={[
        { question: "What schema format is required for conversion?", answer: "An array of flat objects is recommended. Nested objects will be parsed but are best formatted cleanly prior to export." },
        { question: "Is my corporate inventory or private spreadsheet secure?", answer: "Yes, all data conversions run purely in your client RAM without internet requests." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button onClick={process} className="bg-slate-900 hover:bg-slate-800 text-white">Convert JSON to CSV</Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setInput(SAMPLE_JSON_ARRAY); setTimeout(process, 50); }}
              className="text-xs gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset Sample
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output || output.startsWith("Error")}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy CSV"}
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">JSON Input Array</label>
            <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="[ { 'name': 'John' } ]" className="min-h-[220px] font-mono text-xs" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">CSV Output Text</label>
            <Textarea value={output} readOnly className="min-h-[220px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_JSON_XML = `{
  "store": {
    "name": "DevTools Warehouse",
    "branch": "San Francisco",
    "inventoryCount": 540,
    "departments": ["Computers", "Cables", "Peripherals"]
  }
}`;

export function JsonToXmlConverter() {
  const [input, setInput] = useState(SAMPLE_JSON_XML);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const process = () => {
    try {
      const parsed = JSON.parse(input);
      const data = Array.isArray(parsed) ? { root: { item: parsed } } : { root: parsed };
      setOutput(js2xml(data, { compact: true, spaces: 2 }));
    } catch(e:any) {
      setOutput("Error: " + e.message);
    }
  };

  useEffect(() => {
    process();
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="JSON to XML Converter Online - Free Markup Translator"
      description="Instantly convert and translate structured JSON configurations into valid XML tree tags compliant with industry specs. Free and local processing."
      h1="JSON to XML Converter"
      category="JSON / Data"
      categoryPath="/csv-editor"
      howToUse={[
        "Paste your key-value JSON parameters into the input block.",
        "The translator automatically wraps key nodes in corresponding tags and handles array values.",
        "Copy your structured XML code snippet instantly."
      ]}
      faq={[
        { question: "Is a root element added automatically?", answer: "Yes. XML requires a single outer parent container, so the parser wraps everything in a '<root>' tag." },
        { question: "Are special attributes preserved?", answer: "Standard configurations translate directly into nested tag structures for full compatibility." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button onClick={process} className="bg-slate-900 hover:bg-slate-800 text-white">Convert JSON to XML</Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setInput(SAMPLE_JSON_XML); setTimeout(process, 50); }}
              className="text-xs gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset Sample
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output || output.startsWith("Error")}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy XML"}
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">JSON Object Input</label>
            <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="JSON..." className="min-h-[220px] font-mono text-xs" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">XML Output Code</label>
            <Textarea value={output} readOnly className="min-h-[220px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_CSV_DATA = `id,product_name,sku,unit_price,quantity,department,country
1001,Premium Noise-Cancelling Headphones,SKU-NC-402,199.95,45,Audio,USA
1002,Ergonomic Office Chair,SKU-CH-910,249.00,12,Office,Canada
1003,Mechanical Backlit Keyboard,SKU-KB-112,89.50,30,Accessories,USA
1004,Dual-Port USB-C Wall Charger,SKU-CHG-330,35.00,150,Connectivity,Germany
1005,Curved IPS Display Monitor 27\\",SKU-MON-880,280.00,18,Hardware,UK`;

export function CsvToJsonConverter() {
  const [input, setInput] = useState(SAMPLE_CSV_DATA);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const process = () => {
    try {
      const parsed = Papa.parse(input, { header: true });
      setOutput(JSON.stringify(parsed.data, null, 2));
    } catch(e:any) {
      setOutput("Error: " + e.message);
    }
  };

  useEffect(() => {
    process();
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="CSV to JSON Converter Online - Parse Tables and Spreadsheets"
      description="Translate CSV spreadsheet columns or exported databases into standard structured JSON object arrays instantly."
      h1="CSV to JSON Converter"
      category="JSON / Data"
      categoryPath="/csv-editor"
      howToUse={[
        "Paste your comma-separated tabular entries into the CSV text editor.",
        "The engine reads the first row as headings and automatically converts lines into JSON objects.",
        "Copy your structured JSON data array."
      ]}
      faq={[
        { question: "How does the header system operate?", answer: "The parser extracts column fields from line 1 of your text block, mapping them as keys for corresponding elements." },
        { question: "Is my data handled privately?", answer: "Yes, everything runs offline in your browser." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button onClick={process} className="bg-slate-900 hover:bg-slate-800 text-white">Convert CSV to JSON</Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setInput(SAMPLE_CSV_DATA); setTimeout(process, 50); }}
              className="text-xs gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset Sample
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output || output.startsWith("Error")}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy JSON"}
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">CSV Text Input</label>
            <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="name,age\nJohn,30" className="min-h-[220px] font-mono text-xs" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">JSON Array Output</label>
            <Textarea value={output} readOnly className="min-h-[220px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function DataTools1() {
  return (
    <ToolPageLayout
      title="JSON & CSV Data Conversion Tools Online - Format, Validate, Convert"
      description="Convert between JSON, CSV, and XML formats instantly with our free online data tools. Validate, audit, and beautify your raw database entries and spreadsheets effortlessly."
      h1="JSON & CSV Conversion Suite"
      category="JSON / Data"
      categoryPath="/csv-editor"
      howToUse={[
        "Use the respective modules below for JSON validation, JSON to CSV export, JSON to XML representation, or CSV to JSON parsing.",
        "Paste your custom data arrays or structured code into the text input boxes.",
        "Click the conversion/validation buttons to trigger computations in browser memory instantly.",
        "Use the preloaded sample datasets to quickly see expected output formats and start auditing."
      ]}
      faq={[
        { question: "Are my spreadsheets and raw database records secure?", answer: "Yes! Absolutely all JSON parsing, CSV table formatting, and XML conversions are performed completely offline inside your browser RAM. No records are transmitted to external servers." },
        { question: "What is the format used for CSV to JSON converter?", answer: "The parser accepts standard RFC-compliant comma-separated spreadsheet values. It reads the first line as field headers and returns a structured array of JSON key-value objects." },
        { question: "Is the XML converted cleanly?", answer: "Yes, JSON object structures are translated into neat hierarchical XML element nodes, equipped with a top-level root wrapper node for solid syntax compliance." }
      ]}
    >
      <div className="space-y-16">
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs">
          <h2 className="text-xl font-bold text-slate-950 mb-1 flex items-center gap-2">
            1. JSON Syntax Validator & Linter
          </h2>
          <p className="text-xs text-slate-500 mb-6">Verify syntax rules, detect broken brackets, missing commas, or quote issues with precise linter diagnostics.</p>
          <JsonValidator />
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs">
          <h2 className="text-xl font-bold text-slate-950 mb-1 flex items-center gap-2">
            2. JSON-to-CSV Table Exporter
          </h2>
          <p className="text-xs text-slate-500 mb-6">Instantly convert list arrays of structured JSON objects into neat, comma-separated table rows ready for Excel imports.</p>
          <JsonToCsvConverter />
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs">
          <h2 className="text-xl font-bold text-slate-950 mb-1 flex items-center gap-2">
            3. JSON-to-XML Hierarchical Converter
          </h2>
          <p className="text-xs text-slate-500 mb-6">Translate JSON configurations into clean, tagged XML nested hierarchies compliant with standards.</p>
          <JsonToXmlConverter />
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs">
          <h2 className="text-xl font-bold text-slate-950 mb-1 flex items-center gap-2">
            4. CSV-to-JSON Data Parser
          </h2>
          <p className="text-xs text-slate-500 mb-6">Convert spreadsheet rows or database dumps from CSV format into standard, structured JSON object arrays in one click.</p>
          <CsvToJsonConverter />
        </section>
      </div>
    </ToolPageLayout>
  );
}
